import React from "react";
import { UserIcon, CpuChipIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";

/**
 * Message-role presentation. Role is the one place in the trace pane where hue
 * carries information, so it is the one place that gets a hue — and it comes
 * from the `--role-*` tokens (src/styles/tokens.css), not from a palette class
 * picked here (#1545).
 *
 * The accent is a left bar plus a tinted icon on an otherwise plain card, not a
 * full background wash. A wash in five hues is what made the pane read as five
 * products; a 4px bar separates the same five roles while leaving one surface
 * colour on screen. Class strings are spelled out in full because Tailwind's
 * JIT scans source text — a constructed `border-l-role-${role}` would not be
 * generated.
 */
const ROLE_STYLES: Record<
  string,
  { accent: string; label: string; icon: React.ReactNode }
> = {
  user: {
    accent: "border-l-role-user",
    label: "USER",
    icon: <UserIcon className="w-4 h-4 text-role-user" />,
  },
  assistant: {
    accent: "border-l-role-assistant",
    label: "ASSISTANT",
    icon: <CpuChipIcon className="w-4 h-4 text-role-assistant" />,
  },
  system: {
    accent: "border-l-role-system",
    label: "SYSTEM",
    icon: <CpuChipIcon className="w-4 h-4 text-role-system" />,
  },
  tool: {
    accent: "border-l-role-tool",
    label: "TOOL",
    icon: <WrenchScrewdriverIcon className="w-4 h-4 text-role-tool" />,
  },
  tool_call: {
    accent: "border-l-role-tool-call",
    label: "TOOL CALL",
    icon: <WrenchScrewdriverIcon className="w-4 h-4 text-role-tool-call" />,
  },
};

/** Unrecognised roles get the neutral border — no accent, so no invented hue. */
const UNKNOWN_ROLE_ACCENT = "border-l-border";

interface MessageBubbleProps {
  role: string;
  content?: string | Record<string, any>;
  type?: string;
  children?: React.ReactNode;
}

function getRoleStyle(role: string) {
  const known = ROLE_STYLES[role.toLowerCase()];
  if (known) return known;
  return {
    accent: UNKNOWN_ROLE_ACCENT,
    label: (role || "unknown").toUpperCase(),
    icon: null,
  };
}

export function MessageBubble({
  role,
  content,
  type,
  children,
}: MessageBubbleProps) {
  const style = getRoleStyle(role);

  const displayContent = children
    ? children
    : content !== undefined
      ? typeof content === "string"
        ? content
        : JSON.stringify(content, null, 2)
      : "";

  return (
    <div
      className={`rounded-lg border border-border border-l-4 ${style.accent} bg-card overflow-hidden`}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/40">
        {style.icon}
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {style.label}
        </span>
        {type && type !== "text" && (
          <span className="text-xs text-muted-foreground">({type.replace(/_/g, " ")})</span>
        )}
      </div>
      <div className="p-3 text-sm text-foreground whitespace-pre-wrap">
        {displayContent}
      </div>
    </div>
  );
}
